import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { crmPatch } from "@/services/crmApi";
import { capitalise } from "@/lib/names";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { uploadFileResumable } from "@/services/storage";
import { KycDocs } from "@/components/field/KycDocs";
import { kycUrls } from "@/lib/kyc";
import { aiAbout, aiPortfolio, aiPricing, aiSubcats } from "@/services/onboardingApi";

const CITIES = ["Gurgaon", "Delhi", "Noida", "Faridabad", "Ghaziabad"];
const ID_TYPES = ["Aadhaar", "Driving Licence", "Voter ID", "PAN"];
// brugo.in Join-as-a-pro id_type -> onboarding label
const SIGNUP_ID = { aadhaar: "Aadhaar", pan: "PAN", driving_licence: "Driving Licence" };
const TONES = ["Professional", "Friendly", "Confident", "Concise"];

// Skills offered per service category (category name -> specialisations).
const SKILLS_BY_CATEGORY = {
  "Painting": ["Interior Painting", "Exterior Painting", "Textured Painting", "Wall Stencilling", "Decorative Painting", "Waterproof Paint Coating", "Wood Painting & Polishing", "Metal Painting", "Repainting & Touch-ups", "Feature Wall Painting"],
  "Plumbing": ["Pipe Installation", "Pipe Repair", "Leakage Repair", "Bathroom Plumbing", "Kitchen Plumbing", "Drainage & Drain Repair", "Water Tank Plumbing", "Tap & Fixture Installation", "Geyser Plumbing", "Sanitary Fitting"],
  "Electrical": ["House Wiring", "Rewiring", "Lighting Installation", "Switch & Socket Installation", "Fan Installation", "Electrical Panel Work", "MCB & Safety Installation", "Appliance Wiring", "Fault Finding & Repair", "Smart Home Electricals"],
  "Carpentry": ["Custom Furniture", "Modular Furniture", "Wardrobe Installation", "Kitchen Carpentry", "Door Installation & Repair", "Window Carpentry", "Wooden Partition", "False Woodwork & Panelling", "Furniture Repair", "Wood Polishing"],
  "Civil Work": ["Wall Construction", "Wall Repair", "Brickwork", "Plastering", "Concrete Work", "Structural Repair", "Demolition & Breaking", "RCC Work", "Floor Screeding", "Masonry Work"],
  "Flooring & Carpeting": ["Tile Installation", "Tile Replacement", "Marble Installation", "Granite Installation", "Wooden Flooring", "Vinyl Flooring", "Laminate Flooring", "Carpet Installation", "Floor Polishing", "Floor Repair"],
  "Waterproofing": ["Terrace Waterproofing", "Bathroom Waterproofing", "Roof Waterproofing", "Wall Waterproofing", "Basement Waterproofing", "Balcony Waterproofing", "Water Tank Waterproofing", "Leakage Treatment", "Crack Sealing", "Dampness Treatment"],
  "Ceiling": ["POP Ceiling", "Gypsum Ceiling", "False Ceiling", "Cove Lighting Ceiling", "Grid Ceiling", "Wooden Ceiling", "PVC Ceiling", "Ceiling Repair", "Ceiling Panelling", "Decorative Ceiling"],
  "Windows": ["uPVC Windows", "Aluminium Windows", "Sliding Windows", "Casement Windows", "Mesh & Mosquito Screens", "Window Glass Replacement", "Window Repair", "Window Installation", "Window Sealing", "Window Grills"],
  "Glass & Aluminium Fabrication": ["Glass Partitions", "Aluminium Partitions", "Shower Enclosures", "Glass Doors", "Aluminium Doors", "Glass Railings", "Aluminium Windows", "Glass Shelves", "Glass Canopies", "Custom Glass & Aluminium Work"],
  "Metal Fabrication": ["MS Grills", "Gates", "Railings", "Staircase Fabrication", "Metal Partitions", "Balcony Grills", "Structural Metal Work", "Metal Frames", "Custom Metal Furniture", "Welding & Metal Repair"],
  "Decor & Surface Finishes": ["Wallpaper Installation", "Wall Panelling", "Decorative Moulding", "3D Wall Panels", "Designer Wall Finishes", "Microcement Finishes", "Venetian Plaster", "Texture Finishes", "Decorative Borders", "Feature Wall Styling"],
};

const empty = {
  full_name: "", phone: "", category_id: "", category_name: "",
  subcategories: [], skills: [], years_experience: "", about_me: "",
  business_name: "", city: "", service_cities: [], service_radius_km: "",
  indicative_price: "", pricing_items: [], profile_image_url: "", portfolio: [],
  portfolio_note: "", covers_gurgaon: false, kyc: { id_type: "Aadhaar", id_number: "", id_doc_url: "" },
};

const nowISO = () => new Date().toISOString();
const genId = () => "pro-" + (crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "").slice(0, 12) : Math.random().toString(16).slice(2, 14));

function Chip({ active, onClick, children, testid }) {
  return <button type="button" data-testid={testid} onClick={onClick} className={`rounded-full border px-3 py-1 text-xs ${active ? "bg-primary/20 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{children}</button>;
}

export default function FieldOnboarding() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [mine, setMine] = useState([]);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customSub, setCustomSub] = useState("");
  const [tone, setTone] = useState("Professional");
  const [suggested, setSuggested] = useState([]);
  const [ai, setAi] = useState("");
  const [uploadPct, setUploadPct] = useState(0);

  const load = () => {
    adapter.list("categories").then((c) => setCats(c)).catch(() => {});
    const uid = auth.currentUser?.uid;
    adapter.list("professionals")
      .then((all) => setMine(all.filter((p) => p.created_by === uid).sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Opened from Professionals & Field > Joining requests: start with what the person entered on brugo.in.
  const { state: navState } = useLocation();
  const [signupId, setSignupId] = useState(null);
  useEffect(() => {
    const s = navState?.fromSignup;
    if (!s) return;
    setSignupId(s.id);
    setF({
      ...empty,
      full_name: s.full_name || "",
      phone: String(s.phone || "").replace(/\D/g, "").slice(-10),
      category_id: (s.categories || [])[0] || "",
      category_name: (s.category_names || [])[0] || "",
      profile_image_url: s.profile_image_url || "",
      kyc: { id_type: SIGNUP_ID[s.id_type] || "Aadhaar", id_number: s.id_number || "", id_doc_url: s.id_doc_url || "" },
    });
    toast.info("Filled in from the joining request. Check and complete the rest.");
  }, [navState]); // eslint-disable-line react-hooks/exhaustive-deps

  const cat = cats.find((c) => c.id === f.category_id);
  const availSubs = (cat?.subcategories || []);
  const skillOptions = SKILLS_BY_CATEGORY[cat?.name] || [];
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const addKycDoc = (url) => setF((s) => { const list = [...kycUrls(s.kyc), url]; return { ...s, kyc: { ...s.kyc, id_doc_urls: list, id_doc_url: list[0] } }; });
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const upload = async (file, kind, cb) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("File too large (max 50MB)");
    const uid = auth.currentUser?.uid;
    if (!uid) return toast.error("Please sign in again");
    setUploadPct(1);
    try {
      const safe = (file.name || "file").replace(/[^a-zA-Z0-9._-]/g, "");
      const path = `professionals/${uid}/${kind}/${Date.now()}_${safe}`;
      const url = await uploadFileResumable(path, file, (p) => setUploadPct(p));
      cb(url, file.type);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.code === "storage/unauthorized" ? "Storage denied — publish the professionals Storage rule" : (e?.message || "Upload failed"));
    } finally { setUploadPct(0); }
  };

  const resume = async (id) => {
    try {
      const d = await adapter.get("professionals", id);
      setEditId(id);
      setF({
        ...empty, ...d,
        skills: Array.isArray(d.skills) ? d.skills : (typeof d.skills === "string" && d.skills ? d.skills.split(",").map((s) => s.trim()).filter(Boolean) : []),
        kyc: { ...empty.kyc, ...(d.kyc || {}) },
        subcategories: d.subcategories || [], service_cities: d.service_cities || [], pricing_items: d.pricing_items || [], portfolio: d.portfolio || [],
      });
      window.scrollTo(0, 0);
    } catch { toast.error("Could not load"); }
  };
  const reset = () => { setF(empty); setEditId(null); };

  const save = async (status) => {
    if (!f.full_name || !f.phone || !f.category_id || !f.city) return toast.error("Name, phone, category and city are required");
    const uid = auth.currentUser?.uid;
    if (!uid) return toast.error("Please sign in again");
    setBusy(true);
    try {
      const subs = f.subcategories || [];
      const base = {
        ...f,
        category_name: cat?.name || f.category_name,
        phone: String(f.phone).replace(/\D/g, ""),
        skills: Array.isArray(f.skills) ? f.skills : [],
        subcategory: subs[0] || "",
        cities: f.service_cities || [],
        category_ids: f.category_id ? [f.category_id] : [],
        indicative_price: Number(f.indicative_price) || 0,
        onboarding_status: status === "submitted" ? "submitted" : "draft",
        updated_at: nowISO(),
      };
      let id = editId;
      if (!id) {
        id = genId();
        Object.assign(base, {
          professional_id: id, user_id: null, created_by: uid, created_by_role: user?.role || "field",
          verification_status: "pending", status: "inactive",
          rating_average: 0, review_count: 0, completed_project_count: 0, created_at: nowISO(),
        });
        await adapter.set("professionals", id, base);
      } else {
        await adapter.update("professionals", id, base);
      }
      // merge any new subcategories into the category doc
      if (f.category_id && subs.length) {
        try {
          const c = await adapter.get("categories", f.category_id);
          const existing = c?.subcategories || [];
          const merged = [...new Set([...existing, ...subs])];
          if (merged.length !== existing.length) await adapter.update("categories", f.category_id, { subcategories: merged });
        } catch { /* non-blocking */ }
      }
      // activity log
      try {
        await adapter.add("activity_logs", {
          app: "pro", entity_type: "professional", entity_id: id,
          action: status === "submitted" ? "professional_submitted" : "professional_draft_saved",
          change_type: status === "submitted" ? "professional_submitted" : "professional_draft_saved",
          description: `${base.full_name || "(no name)"} — ${base.city || ""}`,
          performed_by: user?.email, changed_by: user?.email, performed_by_role: user?.role || "field",
          status: status === "submitted" ? "pending" : "informational",
          created_at: nowISO(), server_ts: nowISO(),
        });
      } catch { /* non-blocking */ }
      // Link the brugo.in joining request to this professional profile.
      if (signupId) {
        try { await crmPatch("pro_signups", signupId, { professional_id: id, ...(status === "submitted" ? { status: "onboarded" } : {}) }); }
        catch { /* non-blocking */ }
        if (status === "submitted") setSignupId(null);
      }
      toast.success(status === "submitted" ? "Submitted for approval" : "Draft saved");
      setEditId(id); load();
      if (status === "submitted") reset();
    } catch (e) {
      toast.error(e?.code === "permission-denied" ? "Permission denied — your login needs the field/ops role" : (e?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const addPricing = () => set("pricing_items", [...f.pricing_items, { label: "", unit: "", price: "" }]);
  const setPricing = (i, k, v) => set("pricing_items", f.pricing_items.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const genAbout = async () => { setAi("about"); try { set("about_me", await aiAbout({ name: f.full_name, category: cat?.name, subcategories: f.subcategories, skills: f.skills, years: f.years_experience, city: f.city, tone })); } catch { toast.error("AI generation failed"); } finally { setAi(""); } };
  const genPortfolio = async () => { setAi("portfolio"); try { set("portfolio_note", await aiPortfolio({ name: f.full_name, category: cat?.name, work: f.portfolio_note })); } catch { toast.error("AI generation failed"); } finally { setAi(""); } };
  const genPricing = async () => { setAi("pricing"); try { const items = await aiPricing({ category: cat?.name, city: f.city }); set("pricing_items", [...f.pricing_items, ...items]); toast.success(`Added ${items.length} suggestions`); } catch { toast.error("AI generation failed"); } finally { setAi(""); } };
  const genSubs = async () => { if (!f.category_id) return toast.error("Pick a category first"); setAi("subs"); try { const sug = await aiSubcats({ category: cat?.name, existing: [...availSubs, ...f.subcategories] }); setSuggested((s) => [...new Set([...s, ...sug])]); toast.success(`${sug.length} suggestions — tap to add`); } catch { toast.error("AI generation failed"); } finally { setAi(""); } };

  return (
    <div>
      <PageHeader title="Professional Onboarding" subtitle="Onboard a new professional — save a draft or submit for Ops approval." actions={editId ? <Button variant="outline" size="sm" onClick={reset} data-testid="new-blank-btn"><Icons.Plus className="h-4 w-4 mr-1" /> New</Button> : null} />

      {mine.length > 0 && (
        <Card className="p-4 border-border/60 mb-6" data-testid="my-onboardings">
          <h3 className="font-heading font-semibold text-sm mb-3">My onboardings</h3>
          <div className="space-y-2">
            {mine.slice(0, 8).map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border p-2.5" data-testid={`mine-row-${p.id}`}>
                <div><p className="text-sm text-foreground">{p.full_name || "(no name)"} · <span className="text-muted-foreground">{p.city}</span></p><p className="text-[11px] font-mono text-muted-foreground">{p.id} · {p.onboarding_status}{p.correction_note ? ` · note: ${p.correction_note}` : ""}</p></div>
                {["draft", "correction_required"].includes(p.onboarding_status) && <Button size="sm" variant="ghost" onClick={() => resume(p.id)} data-testid={`resume-${p.id}`}>Edit</Button>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* BASIC */}
      <Card className="p-5 border-border/60 mb-4" data-testid="section-basic">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Icons.User className="h-4 w-4 text-indigo-400" /> Basic Information</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="h-16 w-16 rounded-xl bg-secondary overflow-hidden flex items-center justify-center">{f.profile_image_url ? <img src={f.profile_image_url} alt="p" className="w-full h-full object-cover" /> : <Icons.Camera className="h-5 w-5 text-muted-foreground" />}</div>
          <label className="text-sm cursor-pointer rounded-lg border border-border px-3 py-2 hover:bg-accent"><Icons.Upload className="h-4 w-4 inline mr-1" /> Profile photo<input type="file" accept="image/*" className="hidden" data-testid="profile-upload" onChange={(e) => upload(e.target.files[0], "profile", (url) => set("profile_image_url", url))} /></label>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Full name *</Label><Input data-testid="ob-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} onBlur={(e) => set("full_name", capitalise(e.target.value))} /></div>
          <div className="space-y-1.5"><Label>Phone (10-digit) *</Label><Input data-testid="ob-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="98XXXXXXXX" /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Primary category *</Label>
            <Select value={f.category_id} onValueChange={(v) => { set("category_id", v); set("subcategories", []); set("skills", []); }}>
              <SelectTrigger data-testid="ob-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {f.category_id && (
          <div className="mt-3 space-y-1.5"><Label>Sub-categories</Label>
            <div className="flex flex-wrap gap-2" data-testid="ob-subcats">
              {[...new Set([...availSubs, ...suggested, ...f.subcategories])].map((s) => <Chip key={s} active={f.subcategories.includes(s)} onClick={() => toggle("subcategories", s)} testid={`sub-${s}`}>{s}</Chip>)}
            </div>
            <div className="flex gap-2 mt-2"><Input value={customSub} onChange={(e) => setCustomSub(e.target.value)} placeholder="Add custom sub-category" data-testid="ob-customsub" className="max-w-xs" /><Button type="button" size="sm" variant="outline" onClick={() => { if (customSub.trim()) { toggle("subcategories", customSub.trim()); setCustomSub(""); } }} data-testid="ob-addsub">Add</Button><Button type="button" size="sm" variant="ghost" className="text-primary gap-1" disabled={ai === "subs"} onClick={genSubs} data-testid="ai-subs"><Icons.Sparkles className="h-4 w-4" />{ai === "subs" ? "…" : "AI suggest"}</Button></div>
          </div>
        )}
        {/* SKILLS — category-driven multi-select */}
        <div className="mt-3 space-y-1.5">
          <Label>Skills / services you specialise in</Label>
          {!f.category_id ? (
            <p className="text-xs text-muted-foreground">Select a primary category first to see the skills.</p>
          ) : skillOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No preset skills for “{cat?.name}”. Use sub-categories above.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="ob-skills">
              {skillOptions.map((s) => <Chip key={s} active={f.skills.includes(s)} onClick={() => toggle("skills", s)} testid={`skill-${s}`}>{s}</Chip>)}
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="space-y-1.5"><Label>Years of experience</Label><Input data-testid="ob-exp" type="number" value={f.years_experience} onChange={(e) => set("years_experience", e.target.value)} /></div>
        </div>
        <div className="space-y-1.5 mt-3">
          <div className="flex items-center justify-between"><Label>About me</Label>
            <div className="flex items-center gap-2">
              <Select value={tone} onValueChange={setTone}><SelectTrigger className="h-7 w-[130px] text-xs" data-testid="ai-tone"><SelectValue /></SelectTrigger><SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              <Button type="button" size="sm" variant="ghost" className="text-primary gap-1 h-7" disabled={ai === "about"} onClick={genAbout} data-testid="ai-about"><Icons.Sparkles className="h-4 w-4" />{ai === "about" ? "Writing…" : "AI generate"}</Button>
            </div>
          </div>
          <Textarea data-testid="ob-about" rows={5} className="min-h-[120px]" placeholder="A short introduction — experience, specialities and what sets this professional apart (2–4 lines)." value={f.about_me} onChange={(e) => set("about_me", e.target.value)} />
        </div>
      </Card>

      {/* BUSINESS */}
      <Card className="p-5 border-border/60 mb-4" data-testid="section-business">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Icons.Store className="h-4 w-4 text-cyan-400" /> Business Information</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Business / shop name</Label><Input data-testid="ob-business" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} onBlur={(e) => set("business_name", capitalise(e.target.value))} /></div>
          <div className="space-y-1.5"><Label>Service radius (km)</Label><Input data-testid="ob-radius" type="number" value={f.service_radius_km} onChange={(e) => set("service_radius_km", e.target.value)} /></div>
        </div>
        <div className="mt-3 space-y-1.5"><Label>Primary city *</Label><div className="flex flex-wrap gap-2" data-testid="ob-city">{CITIES.map((c) => <Chip key={c} active={f.city === c} onClick={() => set("city", c)} testid={`city-${c}`}>{c}</Chip>)}<Input value={CITIES.includes(f.city) ? "" : f.city} onChange={(e) => set("city", e.target.value)} placeholder="or type" className="max-w-[160px] h-8" /></div></div>
        <div className="mt-3 space-y-1.5"><Label>Also serve cities</Label><div className="flex flex-wrap gap-2" data-testid="ob-serve">{CITIES.map((c) => <Chip key={c} active={f.service_cities.includes(c)} onClick={() => toggle("service_cities", c)}>{c}</Chip>)}</div></div>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-border p-3" data-testid="ob-covers-ggn">
          <div><Label>Can you serve all of Gurgaon?</Label><p className="text-xs text-muted-foreground">Shows a green “Covers GGN” tag to Ops in Professionals & Field.</p></div>
          <Switch checked={!!f.covers_gurgaon} onCheckedChange={(v) => set("covers_gurgaon", v)} data-testid="ob-covers-ggn-switch" />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2"><Label>Indicative pricing</Label><div className="flex gap-2"><Button type="button" size="sm" variant="ghost" className="text-primary gap-1" disabled={ai === "pricing"} onClick={genPricing} data-testid="ai-pricing"><Icons.Sparkles className="h-4 w-4" />{ai === "pricing" ? "…" : "AI suggest"}</Button><Button type="button" size="sm" variant="outline" onClick={addPricing} data-testid="ob-addprice"><Icons.Plus className="h-4 w-4 mr-1" /> Line item</Button></div></div>
          {f.pricing_items.map((p, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 mb-2" data-testid={`price-row-${i}`}>
              <Input placeholder="Label" value={p.label} onChange={(e) => setPricing(i, "label", e.target.value)} />
              <Input placeholder="Unit" value={p.unit} onChange={(e) => setPricing(i, "unit", e.target.value)} />
              <Input placeholder="Price" type="number" value={p.price} onChange={(e) => setPricing(i, "price", e.target.value)} />
            </div>
          ))}
          <div className="space-y-1.5 mt-2"><Label>Indicative price (₹, headline)</Label><Input data-testid="ob-price" type="number" value={f.indicative_price} onChange={(e) => set("indicative_price", e.target.value)} className="max-w-xs" /></div>
        </div>
      </Card>

      {/* PORTFOLIO */}
      <Card className="p-5 border-border/60 mb-4" data-testid="section-portfolio">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Icons.Images className="h-4 w-4 text-emerald-400" /> Portfolio</h3>
        <label className="text-sm cursor-pointer rounded-lg border border-border px-3 py-2 hover:bg-accent inline-flex items-center"><Icons.Upload className="h-4 w-4 mr-1" /> {uploadPct > 0 ? `Uploading… ${uploadPct}%` : "Add photos / videos"}<input type="file" accept="image/*,video/*" multiple className="hidden" data-testid="portfolio-upload" onChange={(e) => Array.from(e.target.files).forEach((file) => upload(file, "portfolio", (url, type) => setF((s) => ({ ...s, portfolio: [...s.portfolio, { url, type }] }))))} /></label>
        <div className="flex flex-wrap gap-2 mt-3">{f.portfolio.map((p, i) => (
          <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-border group" data-testid={`portfolio-item-${i}`}>
            {p.type && p.type.startsWith("video") ? <><video src={p.url} className="w-full h-full object-cover" muted playsInline /><span className="absolute inset-0 flex items-center justify-center bg-black/30"><Icons.Play className="h-5 w-5 text-white" /></span></> : <img src={p.url} alt="" className="w-full h-full object-cover" />}
            <button type="button" onClick={() => setF((s) => ({ ...s, portfolio: s.portfolio.filter((_, idx) => idx !== i) }))} className="absolute top-0 right-0 bg-black/60 rounded-bl px-1 opacity-0 group-hover:opacity-100" data-testid={`portfolio-remove-${i}`}><Icons.X className="h-3 w-3 text-white" /></button>
          </div>
        ))}</div>
        <div className="space-y-1.5 mt-3">
          <div className="flex items-center justify-between"><Label>Description of previous work</Label><Button type="button" size="sm" variant="ghost" className="text-primary gap-1 h-7" disabled={ai === "portfolio"} onClick={genPortfolio} data-testid="ai-portfolio"><Icons.Sparkles className="h-4 w-4" />{ai === "portfolio" ? "Writing…" : "AI generate"}</Button></div>
          <Textarea data-testid="ob-portfolio-note" value={f.portfolio_note} onChange={(e) => set("portfolio_note", e.target.value)} />
        </div>
      </Card>

      {/* KYC */}
      <Card className="p-5 border-border/60 mb-6" data-testid="section-kyc">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Icons.ShieldCheck className="h-4 w-4 text-amber-400" /> Documents / KYC</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Government ID type</Label>
            <Select value={f.kyc.id_type} onValueChange={(v) => set("kyc", { ...f.kyc, id_type: v })}>
              <SelectTrigger data-testid="ob-idtype"><SelectValue /></SelectTrigger>
              <SelectContent>{ID_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>ID number</Label><Input data-testid="ob-idnum" value={f.kyc.id_number} onChange={(e) => set("kyc", { ...f.kyc, id_number: e.target.value })} /></div>
        </div>
        <KycDocs urls={kycUrls(f.kyc)} uploading={uploadPct > 0}
          onAdd={async (files) => { for (const file of files) await upload(file, "kyc", addKycDoc); }}
          onRemove={(u) => setF((s) => { const list = kycUrls(s.kyc).filter((x) => x !== u); return { ...s, kyc: { ...s.kyc, id_doc_urls: list, id_doc_url: list[0] || "" } }; })} />
      </Card>

      <div className="flex gap-3 sticky bottom-4">
        <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => save("draft")} data-testid="save-draft-btn"><Icons.Save className="h-4 w-4" /> Save Draft</Button>
        <Button className="gap-1.5" disabled={busy} onClick={() => save("submitted")} data-testid="submit-approval-btn"><Icons.Send className="h-4 w-4" /> Submit for Approval</Button>
      </div>
    </div>
  );
}
